/* eslint-disable max-len */
import { Action, createReducer, on } from '@ngrx/store';

import { ControlAccessMaxData, ItemsPagination, MtmaMaxData, SettingsCategoryPaging } from 'src/app/model/api/data-rest';
import { PaginationParams } from 'src/app/model/api/pagination-params';
import { BoardUsersInfo } from 'src/app/model/board-users-info';
import { BoardUser } from 'src/app/model/board-user';

import { Command, ConnectedAutomationsCommand } from 'src/app/model/command';
import { Device, supportTasks } from 'src/app/model/device';
import { DeviceFeature } from 'src/app/model/device-feature';
import { DeviceInfo } from 'src/app/model/device-info';
import { DeviceModel, NewDeviceModel } from 'src/app/model/device-model';
import { DeviceState, DeviceStatus } from 'src/app/model/device-status';
import { GateState } from 'src/app/model/gate-state';
import { Manual } from 'src/app/model/manual';
import { ModelState } from 'src/app/model/model-state';
import { RemoteClients, SlotsRange } from 'src/app/model/remote-client';
import { SettingsCategory } from 'src/app/model/settings-category';
import { SignalData } from 'src/app/model/signal-level';
import { User } from 'src/app/model/user';

import * as DeviceActions from './device.actions';
import * as TaskActions from 'src/app/store/task/task.actions';
import * as UserActions from 'src/app/store/user/user.actions';
import { Profile } from 'src/app/model/profile';
import { ETaskStatus } from 'src/app/conf/tasks';
import { SafetyInputCategory } from 'src/app/model/safety-input';
import { GateError } from 'src/app/model/gate-error';
import { MtmaModule, MtmaSettingsModules } from 'src/app/model/mtma-module';
import { MtmaContact } from 'src/app/model/mtma-contact';
import { MtmaContactUnit, MtmaUnit } from 'src/app/model/mtma-unit';
import { MtmaDtmf } from 'src/app/model/mtma-dtmf';
import { MtmaAction } from 'src/app/model/mtma-action';
import { MtmaTag } from 'src/app/model/mtma-tag';
import { MtmaKeypad } from 'src/app/model/mtma-keypad';
import { MtmaTask } from 'src/app/model/mtma-task';
import { DatetimeDeviceData } from 'src/app/model/datetime-device';
import { FirmwaresCategory, FirmwareUpdate } from 'src/app/model/firmware-update';
import { DeviceSpecialDays, DeviceSpecialDaysInfo, DeviceTimers, DeviceTimersInfo, DeviceTimersMeta } from 'src/app/model/device-timer';
import { AccessEvent } from 'src/app/model/access-log';
import { CallUnitEvent } from 'src/app/model/call-access-log';
import { MtmaRule, MtmaTimeslice } from 'src/app/model/mtma-rules-access';
import { CAGroup, CASpecialPlanning, CAWeeklySchedule } from 'src/app/model/control-access';
import { LogThExport } from 'src/app/conf/thermo';
import { ThermoLog } from 'src/app/model/thermo';
import { AutomationsAlias, AutomationsText } from 'src/app/model/automations-alias';
import { Permission } from 'src/app/model/permission';

export interface State {
    devicesStatus: DeviceStatus[];
    currentDevice?: Device;
    currentDeviceLoading: boolean;
    currentDeviceModel?: DeviceModel;
    newDeviceModels?: NewDeviceModel[];
    currentDeviceStatus?: DeviceStatus;
    currentDeviceCommands?: Command[];
    connectedAutomationsCommands?: ConnectedAutomationsCommand[];
    currentDeviceFeatures?: DeviceFeature[];
    currentDeviceInfo?: DeviceInfo;
    currentDeviceSignalData?: SignalData;
    currentDeviceRemoteClients?: RemoteClients;
    currentDeviceFinalUsers?: User[];
    currentDeviceSettingsCategories?: SettingsCategory[];
    currentDeviceSettingsCategoriesPaging?: SettingsCategoryPaging;
    currentDeviceSettingsCategoriesPagingLoading?: boolean;
    currentDeviceSettingsCategoriesPagingError?: boolean;
    currentDeviceBoardUsers?: BoardUser[];
    currentDeviceBoardUsersInfo?: BoardUsersInfo;
    currentDeviceModelManuals?: Manual[];
    currentDeviceModelSettingsCategories?: SettingsCategory[];
    currentDeviceProfiles?: Profile[];
    currentDeviceFinalUserProfiles: Map<number, Profile[]>;
    currentDeviceFinalUserRemoteClients: Map<number, RemoteClients>;
    currentDeviceSafetyInputs: SafetyInputCategory[];
    gateStates: GateState[];
    gateErrors: GateError[];
    configStates: ModelState[];
    settingsModules: ModelState[];
    deviceModels: DeviceModel[];
    errorCodes: ModelState[];
    mtmaActions?: MtmaAction[];
    mtmaCommandAutomation: Command[];
    mtmaModules?: MtmaModule[];
    mtmaSettingsModules?: MtmaSettingsModules[];
    mtmaContacts?: MtmaContact[];
    mtmaContactsAll?: MtmaContact[];
    mtmaContactsPagination?: ItemsPagination;
    mtmaUnits?: MtmaUnit[];
    mtmaUnitsPagination?: ItemsPagination;
    mtmaTags?: MtmaTag[];
    mtmaTagsPagination?: ItemsPagination;
    mtmaTagsAll?: MtmaTag[];
    currentDatetimeDeviceData?: DatetimeDeviceData;

    mtmaDtmfs?: MtmaDtmf[];
    mtmaDtmfsPagination?: ItemsPagination;
    // Mtma Timeslice
    mtmaTimeslices?: MtmaTimeslice[];
    mtmaTimeslicesPagination?: ItemsPagination;
    mtmaTimeslicesAll?: MtmaTimeslice[];
    // Mtma Rules
    mtmaRules?: MtmaRule[];
    mtmaRulesPagination?: ItemsPagination;
    mtmaRulesAll?: MtmaRule[];

    // Mtma Keypads
    mtmaKeypads?: MtmaKeypad[];
    mtmaKeypadsPagination?: ItemsPagination;
    mtmaKeypadsAll?: MtmaKeypad[];

    mtmaContact?: MtmaContact;
    mtmaUnit?: MtmaUnit;
    mtmaDtmf?: MtmaDtmf;
    mtmaTimeslice?: MtmaTimeslice;
    mtmaRule?: MtmaRule;
    mtmaTag?: MtmaTag;
    mtmaKeypad?: MtmaKeypad;

    mtmaTagsRule?: MtmaTag[];
    mtmaKeypadsRule?: MtmaKeypad[];
    mtmaContactsRule?: MtmaContact[];

    mtmaUnitsMaxData?: MtmaMaxData;
    mtmaContactsMaxData?: MtmaMaxData;
    mtmaDtmfsMaxData?: MtmaMaxData;
    mtmaTagsMaxData?: MtmaMaxData;
    mtmaKeypadsMaxData?: MtmaMaxData;
    mtmaTimeslicesMaxData?: MtmaMaxData;
    mtmaRulesMaxData?: MtmaMaxData;
    mtmaKeypadsInvalid?: number;

    mtmaContactModify: boolean;
    mtmaActionSuccess: boolean;
    mtmaUnitModify: boolean;
    mtmaDtmfModify: boolean;
    mtmaTagModify: boolean;
    mtmaKeypadModify: boolean;
    mtmaTimesliceModify: boolean;
    mtmaRuleModify: boolean;
    mtmaCloneModify: boolean;
    firmwareUpdate: FirmwareUpdate[];

    mtmaChanges: boolean;
    mtmaActiveTask: MtmaTask;
    mtmaEditModule: boolean;

    settingsSaved: boolean;
    initializing: boolean;
    readingSignal: boolean;
    addedDeviceId?: number;
    addingDevice: boolean;
    deviceEditing: boolean;
    deviceEdited: boolean;
    inputEditing: boolean;
    inputEdited: boolean;
    outputEditing: boolean;
    outputEdited: boolean;
    settingsLoading: boolean;
    boardUserAdding: boolean;
    boardUserAdded: boolean;
    boardUserEditing: boolean;
    boardUserEdited: boolean;
    boardUserDeleted: boolean;
    boardUserAcquiring: boolean;
    acquiredBoardUser?: BoardUser;
    boardUserAcquired: boolean;
    boardUsersSyncing: boolean;
    boardUsersSynced: boolean;
    boardUsersAdding: boolean;
    boardUsersAdded: boolean;
    boardUsersDeleted: boolean;
    boardUsersLoading: boolean;
    boardUsersTaskRunning: boolean;
    boardUsersItemsPagination?: ItemsPagination;
    boardUsersPaginationParams?: PaginationParams;
    boardUsersInfoLoading: boolean;
    safetyInputsLoading: boolean;
    sharing: boolean;
    shared: boolean;
    currentDeviceTaskSupport: boolean;
    remoteClientDeleting: boolean;
    remoteClientDeleted: boolean;
    shareDeleted: boolean;
    commandsEdited: boolean;
    readingDatetimeDevice: boolean;

    rbeAccessLog?: AccessEvent[];
    rbeAccessLogPagination?: ItemsPagination;
    rbeAccessLogExport?: AccessEvent[];

    callUnitLog?: CallUnitEvent[];
    callUnitLogPagination?: ItemsPagination;

    deviceTimers?: DeviceTimers[];
    deviceTimersMeta?: DeviceTimersMeta;
    deviceTimer?: DeviceTimers;
    deviceTimerModify: boolean;
    deviceSpecialDays?: DeviceSpecialDays[];
    deviceSpecialDaysMeta?: DeviceTimersMeta;
    deviceSpecialDay?: DeviceSpecialDays;
    deviceSpecialDayModify: boolean;
    deviceTimerInfo?: DeviceTimersInfo;
    deviceSpecialDayInfo?: DeviceSpecialDaysInfo;
    isSupportTimers?: boolean;

    automationConnectedNameEdit: boolean;
    deviceState: DeviceState;
    deviceState4: DeviceState;
    accessLog?: AccessEvent[];
    accessLogPagination?: ItemsPagination;
    accessLogExport?: AccessEvent[];
    callUnitLogExport?: CallUnitEvent[];


    newDeviceState: DeviceState;
    newDeviceState0: DeviceState;
    fwVersion: string;
    motorBoardRxVersion: string;
    hwVersion: number;

    passwordValid: boolean;
    passwordChange: boolean;
    passwordRequest: boolean;
    remoteClientLockingUnlocking: boolean;
    remoteClientLockedUnlocked: boolean;

    //--------------------Control Access
    // Groups
    groups?: CAGroup[];
    groupsPagination?: ItemsPagination;
    groupsAll?: CAGroup[];
    group?: CAGroup;
    groupsMaxData?: ControlAccessMaxData;
    groupModify: boolean;
    groupModifing: boolean;
    // WeeklySchedules
    weeklySchedules?: CAWeeklySchedule[];
    weeklySchedulesPagination?: ItemsPagination;
    weeklySchedulesAll?: CAWeeklySchedule[];
    weeklySchedule?: CAWeeklySchedule;
    weeklySchedulesMaxData?: ControlAccessMaxData;
    weeklyScheduleModify: boolean;
    weeklyScheduleModifing: boolean;
    // SpecialPlanning
    specialPlannings?: CASpecialPlanning[];
    specialPlanningsPagination?: ItemsPagination;
    specialPlanningsAll?: CASpecialPlanning[];
    specialPlanning?: CASpecialPlanning;
    specialPlanningsMaxData?: ControlAccessMaxData;
    specialPlanningModify: boolean;
    specialPlanningModifing: boolean;

    acRbeSyncing: boolean;
    acRbeSynced: boolean;
    //--------------------Control Access
    //--------------------Control Access Mtma
    // Mtma Groups
    mtmaGroups?: MtmaRule[];
    mtmaGroupsPagination?: ItemsPagination;
    mtmaGroupsAll?: MtmaRule[];
    mtmaGroup?: MtmaRule;
    mtmaGroupsMaxData?: MtmaMaxData;
    mtmaGroupModify: boolean;
    // Mtma WeeklySchedules
    mtmaWeeklySchedules?: MtmaTimeslice[];
    mtmaWeeklySchedulesPagination?: ItemsPagination;
    mtmaWeeklySchedulesAll?: MtmaTimeslice[];
    mtmaWeeklySchedule?: MtmaTimeslice;
    mtmaWeeklySchedulesMaxData?: MtmaMaxData;
    mtmaWeeklyScheduleModify: boolean;
    // Mtma SpecialPlanning
    mtmaSpecialPlannings?: MtmaTimeslice[];
    mtmaSpecialPlanningsPagination?: ItemsPagination;
    mtmaSpecialPlanningsAll?: MtmaTimeslice[];
    mtmaSpecialPlanning?: MtmaTimeslice;
    mtmaSpecialPlanningsMaxData?: MtmaMaxData;
    mtmaSpecialPlanningModify: boolean;
    //--------------------Control Access Mtma

    loadingThLogExp: boolean;
    loadedThLogExp: boolean;
    logThExport: LogThExport;

    //----- Th log 
    thLogLoading: boolean;
    thLogData: ThermoLog;

    cloneMtmaRunning: boolean;
    automationsFirmwaresList: FirmwaresCategory;

    fwUpdateStart: boolean;
    updatedDeviceAutomationRtc: boolean;
    errorDuplicateCrp: boolean;

    automationAlias: AutomationsAlias;
    automationAliasEditing: boolean;
    automationAliasEdited: boolean;
    automationsText: AutomationsText;
    mtmaPermissions?: Permission[];
    mtmaPermissionsPagination?: ItemsPagination;
    mtmaPermissionsMaxData?: MtmaMaxData;
}

export const initialState: State = {
    devicesStatus: [],
    currentDeviceLoading: false,
    gateStates: [],
    gateErrors: [],
    configStates: [],
    settingsModules: [],
    deviceModels: [],
    errorCodes: [],
    currentDeviceCommands: [],
    connectedAutomationsCommands: [],
    currentDeviceFeatures: [],
    currentDeviceFinalUsers: [],
    currentDeviceSettingsCategories: [],
    currentDeviceSettingsCategoriesPaging: undefined,
    currentDeviceSettingsCategoriesPagingLoading: false,
    currentDeviceSettingsCategoriesPagingError: false,
    currentDeviceBoardUsers: [],
    currentDeviceModelManuals: [],
    currentDeviceModelSettingsCategories: [],
    newDeviceModels: [],
    currentDeviceProfiles: [],
    currentDeviceFinalUserProfiles: new Map<number, Profile[]>(),
    currentDeviceSafetyInputs: [],
    currentDeviceFinalUserRemoteClients: new Map<number, RemoteClients>(),
    mtmaActions: [],
    mtmaCommandAutomation: [],
    mtmaModules: [],
    mtmaContacts: [],
    mtmaContactsAll: [],
    mtmaUnits: [],
    mtmaDtmfs: [],
    mtmaTimeslices: [],
    mtmaRules: [],
    mtmaTags: [],
    mtmaTagsAll: [],
    mtmaKeypads: [],
    mtmaKeypadsAll: [],
    mtmaTagsRule: [],
    mtmaKeypadsRule: [],
    mtmaContactsRule: [],
    firmwareUpdate: [],
    mtmaContact: undefined,
    mtmaUnit: undefined,
    mtmaDtmf: undefined,
    mtmaTimeslice: undefined,
    mtmaRule: undefined,
    mtmaTag: undefined,
    mtmaKeypad: undefined,
    mtmaContactModify: false,
    mtmaActionSuccess: false,
    mtmaUnitModify: false,
    mtmaDtmfModify: false,
    mtmaTimesliceModify: false,
    mtmaRuleModify: false,
    mtmaTagModify: false,
    mtmaKeypadModify: false,
    mtmaCloneModify: false,
    mtmaChanges: false,
    mtmaActiveTask: new MtmaTask(),
    mtmaEditModule: false,
    settingsSaved: false,
    initializing: false,
    readingSignal: false,
    addingDevice: false,
    deviceEditing: false,
    deviceEdited: false,
    inputEditing: false,
    inputEdited: false,
    outputEditing: false,
    outputEdited: false,
    settingsLoading: false,
    boardUserAdding: false,
    boardUserAdded: false,
    boardUserEditing: false,
    boardUserEdited: false,
    boardUserDeleted: false,
    boardUserAcquiring: false,
    boardUserAcquired: false,
    boardUsersSyncing: false,
    boardUsersSynced: false,
    boardUsersAdding: false,
    boardUsersAdded: false,
    boardUsersDeleted: false,
    boardUsersLoading: false,
    boardUsersTaskRunning: false,
    boardUsersInfoLoading: false,
    safetyInputsLoading: false,
    sharing: false,
    shared: false,
    currentDeviceTaskSupport: false,
    remoteClientDeleting: false,
    remoteClientDeleted: false,
    shareDeleted: false,
    commandsEdited: false,
    readingDatetimeDevice: false,

    rbeAccessLog: [],
    rbeAccessLogExport: [],
    callUnitLog: [],
    deviceTimers: [],
    deviceTimer: undefined,
    deviceTimerModify: false,
    deviceSpecialDays: [],
    deviceSpecialDay: undefined,
    deviceSpecialDayModify: false,
    deviceTimerInfo: undefined,
    deviceTimersMeta: undefined,
    deviceSpecialDaysMeta: undefined,
    deviceSpecialDayInfo: undefined,
    isSupportTimers: false,

    automationConnectedNameEdit: false,
    deviceState: undefined,
    deviceState4: undefined,
    accessLog: [],
    accessLogExport: [],
    passwordValid: false,
    passwordChange: false,
    passwordRequest: false,

    newDeviceState: undefined,
    newDeviceState0: undefined,
    fwVersion: '',
    motorBoardRxVersion: '',
    hwVersion: null,
    errorDuplicateCrp: undefined,

    //--------------------Control Access
    // Groups
    groups: [],
    groupsAll: [],
    group: undefined,
    groupModify: false,
    groupModifing: false,
    // WeeklySchedules
    weeklySchedules: [],
    weeklySchedulesAll: [],
    weeklySchedule: undefined,
    weeklyScheduleModify: false,
    weeklyScheduleModifing: false,
    // SpecialPlanning
    specialPlannings: [],
    specialPlanningsAll: [],
    specialPlanning: undefined,
    specialPlanningModify: false,
    specialPlanningModifing: false,

    acRbeSyncing: false,
    acRbeSynced: false,
    //--------------------Control Access
    //--------------------Control Access Mtma
    // Groups
    mtmaGroups: [],
    mtmaGroupsAll: [],
    mtmaGroup: undefined,
    mtmaGroupModify: false,
    // WeeklySchedules
    mtmaWeeklySchedules: [],
    mtmaWeeklySchedulesAll: [],
    mtmaWeeklySchedule: undefined,
    mtmaWeeklyScheduleModify: false,
    // SpecialPlanning
    mtmaSpecialPlannings: [],
    mtmaSpecialPlanningsAll: [],
    mtmaSpecialPlanning: undefined,
    mtmaSpecialPlanningModify: false,
    //--------------------Control Access Mtma

    loadingThLogExp: false,
    loadedThLogExp: false,
    logThExport: new LogThExport(),
    remoteClientLockingUnlocking: false,
    remoteClientLockedUnlocked: false,

    //----- Th log 
    thLogLoading: false,
    thLogData: null,

    cloneMtmaRunning: false,
    automationsFirmwaresList: undefined,
    fwUpdateStart: false,
    updatedDeviceAutomationRtc: false,

    automationAlias: new AutomationsAlias(),
    automationAliasEditing: false,
    automationAliasEdited: false,
    automationsText: new AutomationsText(),

    mtmaPermissions: [],
};

const deviceReducer = createReducer(
    initialState,
    on(DeviceActions.deviceDialogReset, (state) => ({
        ...state,
        addingDevice: false, addedDeviceId: undefined,
        deviceEditing: false, deviceEdited: false, errorDuplicateCrp: undefined,
    })),
    on(DeviceActions.addDevice, (state) => ({ ...state, errorDuplicateCrp: undefined })),
    on(DeviceActions.addDeviceFailed, (state, payload) => ({ ...state, addingDevice: false, addedDeviceId: undefined, errorDuplicateCrp: payload.errorDuplicateCrp })),
    on(DeviceActions.addDeviceSuccess, (state, payload) => ({ ...state, addingDevice: false, addedDeviceId: payload.device.Id })),
    on(DeviceActions.editDevice, (state) => ({ ...state, deviceEditing: true, deviceEdited: false, errorDuplicateCrp: undefined })),
    on(DeviceActions.editDeviceSuccess, (state, payload) => {
        const currentDevice = state.currentDevice;
        currentDevice.Name = payload.device.Name;
        currentDevice.Description = payload.device.Description;
        return {
            ...state, currentDevice: currentDevice, deviceEditing: false, deviceEdited: true
        };
    }),
    on(DeviceActions.editDeviceFailed, (state, payload) => ({ ...state, deviceEditing: false, deviceEdited: false, errorDuplicateCrp: payload.errorDuplicateCrp })),
    on(DeviceActions.getDevicesStatusSuccess, (state, payload) => ({ ...state, devicesStatus: payload.devicesStatus })),
    on(DeviceActions.updateCurrentDeviceStatusSuccess, (state, payload) => {
        const indx = state.devicesStatus.findIndex(ds => state.currentDevice !== undefined && ds.Id === state.currentDevice.Id);
        if (indx < 0) {
            return { ...state, devicesStatus: [payload.deviceStatus], currentDeviceStatus: payload.deviceStatus };
        } else {
            const newDevicesStatus = state.devicesStatus.filter(ds => state.currentDevice === undefined || ds.Id !== state.currentDevice.Id);
            return {
                ...state,
                devicesStatus: [...newDevicesStatus, payload.deviceStatus],
                currentDeviceStatus: payload.deviceStatus
            };
        }
    }),
    on(DeviceActions.getCurrentDevice, (state) => ({
        ...state,
        currentDevice: undefined, currentDeviceModel: undefined, currentDeviceStatus: undefined,
        currentDeviceCommands: [], currentDeviceFeatures: [], currentDeviceInfo: undefined,
        currentDeviceSignalData: undefined, currentDeviceLoading: true
    })),
    on(DeviceActions.getCurrentDeviceSuccess, (state, payload) => {
        const currentDeviceStatus = state.devicesStatus.find(ds => ds.Id === payload.device.Id);
        return {
            ...state,
            currentDevice: payload.device,
            currentDeviceStatus,
            currentDeviceLoading: false,
            currentDeviceTaskSupport: supportTasks(payload.device),
            automationConnectedNameEdit: false,
        };
    }),
    on(DeviceActions.getCurrentDeviceFailed, (state) => ({
        ...state, currentDeviceLoading: false
    })),
    on(DeviceActions.getDeviceModelSuccess, (state, payload) => ({ ...state, currentDeviceModel: payload.deviceModel })),
    on(DeviceActions.getNewDeviceModelsSuccess, (state, payload) => ({ ...state, newDeviceModels: payload.deviceModel })),
    on(DeviceActions.getGateStatesSuccess, (state, payload) => ({ ...state, gateStates: payload.gateStates })),
    on(DeviceActions.getGateErrorsSuccess, (state, payload) => ({ ...state, gateErrors: payload.gateErrors })),
    on(DeviceActions.getConfigStatesSuccess, (state, payload) => ({ ...state, configStates: payload.configStates })),
    on(DeviceActions.initDevice, (state) => ({ ...state, initializing: true })),
    on(DeviceActions.initDeviceSuccess, (state) => ({ ...state, initializing: false })),
    on(DeviceActions.initDeviceFailed, (state) => ({ ...state, initializing: false })),
    on(DeviceActions.getSignalLevel, (state) => ({ ...state, readingSignal: true })),
    on(DeviceActions.getSignalLevelSuccess, (state, payload) => ({
        ...state,
        currentDeviceSignalData: payload.signalData,
        readingSignal: false,
    })),
    on(DeviceActions.getSignalLevelFailed, (state) => ({ ...state, readingSignal: false })),
    on(DeviceActions.getCommandsSuccess, (state, payload) => ({ ...state, currentDeviceCommands: payload.commands, connectedAutomationsCommands: payload.connectedAutomationsCommand })),
    on(DeviceActions.getFeaturesSuccess, (state, payload) => ({ ...state, currentDeviceFeatures: payload.features })),
    on(DeviceActions.getRemoteClientsSuccess, (state, payload) => ({ ...state, currentDeviceRemoteClients: payload.remoteClients })),
    on(DeviceActions.getFinalUserRemoteClientsSuccess, (state, payload) => {
        const a = new Map(state.currentDeviceFinalUserRemoteClients).set(
            payload.userId,
            { ...payload.remoteClients, SlotsRange: SlotsRange(payload.remoteClients) });
        return { ...state, currentDeviceFinalUserRemoteClients: a };
    }),
    on(DeviceActions.getDeviceInfoSuccess, (state, payload) => ({ ...state, currentDeviceInfo: payload.deviceInfo })),
    on(DeviceActions.getDeviceModelsSuccess, (state, payload) => ({ ...state, deviceModels: payload.deviceModels })),
    on(DeviceActions.getDeviceModelSettings, (state) => ({ ...state, settingsLoading: true })),
    on(DeviceActions.getDeviceModelSettingsSuccess, (state, payload) => ({
        ...state, currentDeviceSettingsCategories: payload.SettingsCategories, settingsLoading: false,
    })),
    on(DeviceActions.getDeviceModelSettingsFailed, (state) => ({
        ...state, settingsLoading: false
    })),
    on(DeviceActions.ioDialogReset, (state) => ({
        ...state,
        inputEditing: false, inputEdited: false, outputEditing: false, outputEdited: false
    })),
    on(DeviceActions.editMultiIO, (state) => ({
        ...state,
        inputEditing: false, inputEdited: true, outputEditing: false, outputEdited: true
    })),
    on(DeviceActions.getFinalUsersSuccess, (state, payload) => ({ ...state, currentDeviceFinalUsers: payload.finalUsers })),
    on(DeviceActions.getDeviceSettings, (state) => ({ ...state, settingsLoading: true })),
    on(DeviceActions.getDeviceSettingsSuccess, (state, payload) => ({
        ...state, currentDeviceSettingsCategories: payload.SettingsCategories, settingsLoading: false,
    })),
    on(DeviceActions.getDeviceSettingsFailed, (state) => ({ ...state, settingsLoading: false })),
    on(DeviceActions.saveDeviceSettings, (state) => ({ ...state, settingsSaved: false })),
    on(DeviceActions.saveDeviceSettingsSuccess, (state) => ({ ...state, settingsSaved: true })),
    on(DeviceActions.getDeviceModelManualsSuccess, (state, payload) => ({ ...state, currentDeviceModelManuals: payload.manuals })),
    on(DeviceActions.getErrorCodesSuccess, (state, payload) => ({ ...state, errorCodes: payload.errorCodes })),
    on(DeviceActions.getBoardusers, (state, payload) => ({
        ...state, boardUsersPaginationParams: payload.paging, boardUsersLoading: true, boardUserDeleted: false, boardUsersDeleted: false,
    })),
    on(DeviceActions.getBoardusersSuccess, (state, payload) => ({
        ...state, currentDeviceBoardUsers: payload.boardUsers, boardUsersItemsPagination: payload.paging, boardUsersLoading: false,
    })),
    on(DeviceActions.getBoardusersFailed, (state) => ({ ...state, boardUsersLoading: false })),
    on(DeviceActions.getBoardusersInfoFailed, (state) => ({
        ...state, boardUsersInfoLoading: true,
    })),
    on(DeviceActions.getBoardusersInfoSuccess, (state, payload) => ({
        ...state, currentDeviceBoardUsersInfo: payload.boardUsersInfo, boardUsersInfoLoading: false
    })),
    on(DeviceActions.getBoardusersInfoFailed, (state) => ({ ...state, boardUsersInfoLoading: false })),
    on(DeviceActions.boardUserDialogReset, (state) => ({
        ...state, boardUserEditing: false, boardUserEdited: false, boardUserAdding: false, boardUserAdded: false
    })),
    on(DeviceActions.deleteBoardUserSuccess, (state) => ({
        ...state, boardUserDeleted: true,
    })),
    on(DeviceActions.deleteBoardUserSelectedSuccess, (state) => ({
        ...state, boardUsersDeleted: true,
    })),
    on(DeviceActions.addBoardUser, (state) => ({ ...state, boardUserAdding: true, boardUserAdded: false })),
    on(DeviceActions.addBoardUserSuccess, (state) => ({ ...state, boardUserAdding: false, boardUserAdded: true })),
    on(DeviceActions.addBoardUserFailed, (state) => ({ ...state, boardUserAdding: false, boardUserAdded: false })),
    on(DeviceActions.editBoardUser, (state) => ({ ...state, boardUserEditing: true, boardUserEdited: false })),
    on(DeviceActions.editBoardUserSuccess, (state) => ({ ...state, boardUserEditing: false, boardUserEdited: true })),
    on(DeviceActions.editBoardUserFailed, (state) => ({ ...state, boardUserEditing: false, boardUserEdited: false })),

    on(DeviceActions.resetBoarduserAcquire, (state) => ({ ...state, boardUserAcquiring: false, boardUserAcquired: false })),
    on(DeviceActions.acquireBoardUser, (state) => ({ ...state, boardUserAcquiring: true, boardUserAcquired: false })),
    on(DeviceActions.acquireBoardUserSuccess, (state, payload) => ({
        ...state, acquiredBoardUser: payload.boardUser, boardUserAcquiring: false, boardUserAcquired: true
    })),
    on(DeviceActions.acquireBoardUserFailed, (state) => ({ ...state, boardUserAcquiring: false, boardUserAcquired: true })),
    on(DeviceActions.syncBoardUsers, (state) => ({
        ...state, boardUsersTaskRunning: true, boardUsersSyncing: true, boardUsersSynced: false
    })),
    on(DeviceActions.syncBoardUsersSuccess, (state) => ({
        ...state, boardUsersTaskRunning: true, boardUsersSyncing: false, boardUsersSynced: true
    })),
    on(DeviceActions.syncBoardUsersFailed, (state) => ({
        ...state, boardUsersTaskRunning: false, boardUsersSyncing: false, boardUsersSynced: false
    })),
    on(DeviceActions.getProfilesSuccess, (state, payload) => ({ ...state, currentDeviceProfiles: payload.profiles })),
    on(DeviceActions.getUserProfilesSuccess, (state, payload) => {
        const a = new Map(state.currentDeviceFinalUserProfiles).set(payload.userId, payload.profiles);
        return { ...state, currentDeviceFinalUserProfiles: a };
    }),
    on(DeviceActions.updateUserProfileSuccess, (state, payload) => {
        const b = new Map(state.currentDeviceFinalUserProfiles);
        const userProfiles = b.get(payload.userId);
        userProfiles.map((profile) => {
            if (profile.Id === payload.id) {
                return Object.assign(profile, { Checked: payload.checked });
            }
            return profile;
        });
        b.set(payload.userId, userProfiles);
        return {
            ...state,
            currentDeviceFinalUserProfiles: b
        };
    }),
    on(DeviceActions.clearUserProfiles, (state, payload) => {
        const c = new Map(state.currentDeviceFinalUserProfiles).set(payload.userId, []);
        return { ...state, currentDeviceFinalUserProfiles: c };
    }),
    on(DeviceActions.resetShareDialog, (state) => ({ ...state, sharing: false, shared: false })),
    on(DeviceActions.shareDevice, (state) => ({ ...state, sharing: true, shared: false })),
    on(DeviceActions.shareDeviceSuccess, (state) => ({ ...state, sharing: false, shared: true })),
    on(DeviceActions.shareDeviceFailed, (state) => ({ ...state, sharing: false, shared: false })),
    on(DeviceActions.removeShareDeviceSuccess, (state, payload) => ({
        ...state, currentDeviceFinalUsers: state.currentDeviceFinalUsers.filter(fu => fu.Id !== payload.userId)
    })),
    on(UserActions.revokeShareInvitationSuccess, (state, payload) => ({
        ...state, currentDeviceFinalUsers: state.currentDeviceFinalUsers.filter(fu => fu.Email !== payload.email)
    })),
    on(DeviceActions.addBoardUsers, (state) => ({ ...state, boardUsersAdding: true, boardUsersAdded: false })),
    on(DeviceActions.addBoardUsersSuccess, (state) => ({ ...state, boardUsersAdding: false, boardUsersAdded: true })),
    on(DeviceActions.addBoardUsersFailed, (state) => ({ ...state, boardUsersAdding: false, boardUsersAdded: false })),
    /*on(TaskActions.getTasksSuccess, (state, payload) => {
        const anyTask = payload.tasks.filter(task => state.currentDevice !== undefined && task.DeviceId === state.currentDevice.Id && task.Status === ETaskStatus.RUNNING);
        return { ...state, boardUsersTaskRunning: anyTask.length > 0 };
    }),*/
    on(TaskActions.getActiveTasksSuccess, (state, payload) => {
        const anyTask = payload.tasks.filter(task => state.currentDevice !== undefined && task.DeviceId === state.currentDevice.Id && (task.Status === ETaskStatus.RUNNING || task.Status === ETaskStatus.PENDING));
        return { ...state, boardUsersTaskRunning: anyTask.length > 0 };
    }),
    on(DeviceActions.getSafetyInputs, (state) => ({ ...state, safetyInputsLoading: true })),
    on(DeviceActions.getSafetyInputsSuccess, (state, payload) => ({
        ...state, currentDeviceSafetyInputs: payload.safetyInputs, safetyInputsLoading: false,
    })),
    on(DeviceActions.getSafetyInputsFailed, (state) => ({ ...state, safetyInputsLoading: false })),
    on(DeviceActions.deleteRemoteClient, (state) => ({ ...state, remoteClientDeleting: true, remoteClientDeleted: false })),
    on(DeviceActions.deleteRemoteClientSuccess, (state) => ({ ...state, remoteClientDeleting: false, remoteClientDeleted: true })),
    on(DeviceActions.deleteRemoteClientFailed, (state) => ({ ...state, remoteClientDeleting: false, remoteClientDeleted: false })),
    on(DeviceActions.deletingSharedDevice, (state) => ({ ...state, shareDeleted: false })),
    on(DeviceActions.deleteSharedDeviceSuccess, (state) => ({ ...state, shareDeleted: true })),
    on(DeviceActions.deleteSharedDeviceFailed, (state) => ({ ...state, shareDeleted: false })),
    on(DeviceActions.resetEditCommands, (state) => ({ ...state, commandsEdited: false })),
    on(DeviceActions.editCommandsSuccess, (state, payload) => ({
        ...state, commandsEdited: true, currentDeviceCommands: payload.commands
    })),
    on(DeviceActions.editCommandsFailed, (state) => ({ ...state, commandsEdited: false })),
    on(DeviceActions.getMtmaActionsSuccess, (state, payload) => ({ ...state, mtmaActions: payload.actions })),
    on(DeviceActions.getMtmaModulesSuccess, (state, payload) => ({ ...state, mtmaModules: payload.modules })),
    on(DeviceActions.getMtmaContactsSuccess, (state, payload) => ({
        ...state,
        mtmaContacts: payload.contacts, mtmaContactsPagination: payload.paging, mtmaContactModify: false,
        mtmaContactsMaxData: payload.maxData
    })),
    on(DeviceActions.getMtmaContactsAllSuccess, (state, payload) => ({
        ...state,
        mtmaContactsAll: payload.contacts
    })),
    on(DeviceActions.resetContactForm, (state) => ({
        ...state, mtmaContact: undefined, mtmaContactModify: false,
    })),
    on(DeviceActions.addMtmaContactSuccess, (state) => ({
        ...state, mtmaContact: undefined, mtmaContactModify: true
    })),
    on(DeviceActions.editMtmaContactSuccess, (state) => ({
        ...state, mtmaContact: undefined, mtmaContactModify: true
    })),
    on(DeviceActions.deleteMtmaContactSuccess, (state) => ({
        ...state, mtmaContact: undefined, mtmaContactModify: true
    })),
    on(DeviceActions.getMtmaAutomationCommands, (state) => ({
        ...state, mtmaActionSuccess: false
    })),
    on(DeviceActions.getMtmaAutomationCommandsSuccess, (state, payload) => {
        const myClonedArray = [...state.mtmaCommandAutomation];
        for (const command of payload.commands) {
            command.AutomationId = payload.automationId;
            myClonedArray.push(command);
        }
        return { ...state, mtmaCommandAutomation: myClonedArray, mtmaActionSuccess: true };
    }),
    on(DeviceActions.resetUnitForm, (state) => ({
        ...state, mtmaUnit: undefined, mtmaUnitModify: false,
    })),
    on(DeviceActions.resetUpdateUnit, (state) => ({
        ...state, mtmaUnit: undefined, mtmaUnitModify: true,
    })),
    on(DeviceActions.getMtmaUnitsSuccess, (state, payload) => ({
        ...state,
        mtmaUnits: payload.units, mtmaUnitsPagination: payload.paging, mtmaUnitModify: false,
        mtmaUnitsMaxData: payload.maxData
    })),
    on(DeviceActions.addMtmaUnitSuccess, (state) => ({
        ...state, mtmaUnit: undefined, mtmaUnitModify: true
    })),
    on(DeviceActions.editMtmaUnitSuccess, (state) => ({
        ...state, mtmaUnit: undefined, mtmaUnitModify: true
    })),
    on(DeviceActions.deleteMtmaUnitSuccess, (state) => ({
        ...state, mtmaUnit: undefined, mtmaUnitModify: true
    })),
    on(DeviceActions.getMtmaUnitSuccess, (state, payload) => {
        payload.unitRequest.ContactsInfo = new Array<MtmaContactUnit>();
        for (const contact of payload.unitResponse.Contacts) {
            payload.unitRequest.ContactsInfo.push(contact as MtmaContactUnit);
        }
        return { ...state, };
    }),
    on(DeviceActions.getMtmaContactSuccess, (state, payload) => {
        payload.contactRequest.Units = new Array<MtmaUnit>();
        for (const unit of payload.contactResponse.Units) {
            payload.contactRequest.Units.push(unit);
        }
        return { ...state, };
    }),
    on(DeviceActions.getMtmaDtmfsSuccess, (state, payload) => ({
        ...state,
        mtmaDtmfs: payload.dtmfs, mtmaDtmfsPagination: payload.paging, mtmaDtmfModify: false,
        mtmaDtmfsMaxData: payload.maxData
    })),
    on(DeviceActions.resetDtmfForm, (state) => ({
        ...state, mtmaDtmf: undefined, mtmaDtmfModify: false,
    })),
    on(DeviceActions.addMtmaDtmfSuccess, (state) => ({
        ...state, mtmaDtmf: undefined, mtmaDtmfModify: true
    })),
    on(DeviceActions.editMtmaDtmfSuccess, (state) => ({
        ...state, mtmaDtmf: undefined, mtmaDtmfModify: true
    })),
    on(DeviceActions.deleteMtmaDtmfSuccess, (state) => ({
        ...state, mtmaDtmf: undefined, mtmaDtmfModify: true
    })),

    on(DeviceActions.getMtmaTagsSuccess, (state, payload) => ({
        ...state,
        mtmaTags: payload.tags, mtmaTagsPagination: payload.paging, mtmaTagModify: false,
        mtmaTagsMaxData: payload.maxData
    })),
    on(DeviceActions.resetTagForm, (state) => ({
        ...state, mtmaTag: undefined, mtmaTagModify: false,
    })),
    on(DeviceActions.addMtmaTagSuccess, (state) => ({
        ...state, mtmaTag: undefined, mtmaTagModify: true
    })),
    on(DeviceActions.editMtmaTagSuccess, (state) => ({
        ...state, mtmaTag: undefined, mtmaTagModify: true
    })),
    on(DeviceActions.deleteMtmaTagSuccess, (state) => ({
        ...state, mtmaTag: undefined, mtmaTagModify: true
    })),
    on(DeviceActions.getMtmaTagsAllSuccess, (state, payload) => ({
        ...state,
        mtmaTagsAll: payload.tags
    })),

    on(DeviceActions.getMtmaKeypadsSuccess, (state, payload) => ({
        ...state,
        mtmaKeypads: payload.keypads, mtmaKeypadsPagination: payload.paging, mtmaKeypadModify: false,
        mtmaKeypadsMaxData: payload.maxData, mtmaKeypadsInvalid: payload.invalid
    })),
    on(DeviceActions.resetKeypadForm, (state) => ({
        ...state, mtmaKeypad: undefined, mtmaKeypadModify: false,
    })),
    on(DeviceActions.addMtmaKeypadSuccess, (state) => ({
        ...state, mtmaKeypad: undefined, mtmaKeypadModify: true
    })),
    on(DeviceActions.editMtmaKeypadSuccess, (state) => ({
        ...state, mtmaKeypad: undefined, mtmaKeypadModify: true
    })),
    on(DeviceActions.deleteMtmaKeypadSuccess, (state) => ({
        ...state, mtmaKeypad: undefined, mtmaKeypadModify: true
    })),
    on(DeviceActions.getMtmaKeypadsAllSuccess, (state, payload) => ({
        ...state,
        mtmaKeypadsAll: payload.keypads
    })),
    on(DeviceActions.getMtmaSettingsModulesSuccess, (state, payload) => ({
        ...state, mtmaSettingsModules: payload.listModulesSettings
    })),
    on(DeviceActions.getMtmaChangesSuccess, (state, payload) => ({
        ...state, mtmaChanges: payload.changes.Changes
    })),
    on(DeviceActions.getMtmaActiveTaskSuccess, (state, payload) => ({
        ...state, mtmaActiveTask: payload.task
    })),
    on(DeviceActions.mtmaWriteConfigSuccess, (state, payload) => ({
        ...state, mtmaActiveTask: payload.task
    })),
    on(DeviceActions.getMtmaAbortSuccess, (state) => ({
        ...state, mtmaActiveTask: new MtmaTask()
    })),

    // MTMA Timeslice
    on(DeviceActions.getMtmaTimeSlicesSuccess, (state, payload) => ({
        ...state,
        mtmaTimeslices: payload.timeslices, mtmaTimeslicesPagination: payload.paging,
        mtmaTimesliceModify: false, mtmaTimeslicesMaxData: payload.maxData
    })),
    on(DeviceActions.resetTimeSliceForm, (state) => ({
        ...state, mtmaTimeSlice: undefined, mtmaTimesliceModify: false,
    })),
    on(DeviceActions.addMtmaTimeSliceSuccess, (state) => ({
        ...state, mtmaTimeSlice: undefined, mtmaTimesliceModify: true
    })),
    on(DeviceActions.editMtmaTimeSliceSuccess, (state) => ({
        ...state, mtmaTimeSlice: undefined, mtmaTimesliceModify: true
    })),
    on(DeviceActions.deleteMtmaTimeSliceSuccess, (state) => ({
        ...state, mtmaTimeSlice: undefined, mtmaTimesliceModify: true, mtmaRuleModify: true
    })),
    // Mtma Rules
    on(DeviceActions.getMtmaRulesSuccess, (state, payload) => ({
        ...state,
        mtmaRules: payload.rules, mtmaRulesPagination: payload.paging,
        mtmaRuleModify: false, mtmaRulesMaxData: payload.maxData
    })),
    on(DeviceActions.resetRuleForm, (state) => ({
        ...state, mtmaRule: undefined, mtmaRuleModify: false,
    })),
    on(DeviceActions.addMtmaRuleSuccess, (state) => ({
        ...state, mtmaRule: undefined, mtmaRuleModify: true
    })),
    on(DeviceActions.editMtmaRuleSuccess, (state) => ({
        ...state, mtmaRule: undefined, mtmaRuleModify: true
    })),
    on(DeviceActions.deleteMtmaRuleSuccess, (state) => ({
        ...state, mtmaRule: undefined, mtmaRuleModify: true
    })),
    // Mtma Rules Access
    on(DeviceActions.resetMtmaRulesAccessForm, (state) => ({
        ...state, mtmaTagsRule: undefined, mtmaKeypadsRule: undefined, mtmaContactsRule: undefined,
    })),
    on(DeviceActions.getMtmaRulesAccessSuccess, (state, payload) => ({
        ...state, mtmaTagsRule: payload.tags, mtmaKeypadsRule: payload.keypads, mtmaContactsRule: payload.contacts,
    })),
    on(DeviceActions.cloneMtma, (state) => ({
        ...state, mtmaCloneModify: false, cloneMtmaRunning: true,
    })),
    on(DeviceActions.resetCloneMtmaForm, (state) => ({
        ...state, mtmaCloneModify: false, cloneMtmaRunning: false,
    })),
    on(DeviceActions.cloneMtmaSuccess, (state) => ({
        ...state, cloneMtmaRunning: false, mtmaCloneModify: true,
    })),
    on(DeviceActions.cloneMtmaFailed, (state) => ({
        ...state, cloneMtmaRunning: false,
    })),
    on(DeviceActions.resetEditMtmaModule, (state) => ({
        ...state, mtmaEditModule: false,
    })),
    on(DeviceActions.editMtmaModuleSuccess, (state) => ({
        ...state, mtmaEditModule: true,
    })),
    on(DeviceActions.resetEditParamMtmaModule, (state) => ({
        ...state, mtmaEditModule: false,
    })),
    on(DeviceActions.editParamMtmaModuleSuccess, (state) => ({
        ...state, mtmaEditModule: true,
    })),
    on(DeviceActions.getMtmaTimeSlicesAllSuccess, (state, payload) => ({
        ...state,
        mtmaTimeslicesAll: payload.timeslices
    })),
    on(DeviceActions.editMtmaRulesAccessSuccess, (state) => ({
        ...state, mtmaRuleModify: true, mtmaContactModify: true, mtmaKeypadModify: true, mtmaTagModify: true,
    })),
    on(DeviceActions.getMtmaRulesAllSuccess, (state, payload) => ({
        ...state,
        mtmaRulesAll: payload.rules
    })),
    on(DeviceActions.getDatetimeDevice, (state) => ({ ...state, readingDatetimeDevice: true })),
    on(DeviceActions.getDatetimeDeviceSuccess, (state, payload) => ({
        ...state,
        currentDatetimeDeviceData: payload.datetimeDevice,
        readingDatetimeDevice: false,
    })),
    on(DeviceActions.getDatetimeDeviceFailed, (state) => ({ ...state, readingDatetimeDevice: false })),
    on(DeviceActions.resetFirmwareUpdate, (state) => ({ ...state, firmwareUpdate: [] })),
    on(DeviceActions.getFirmwareUpdateSuccess, (state, payload) => ({ ...state, firmwareUpdate: payload.listUpdateFirmware !== undefined ? payload.listUpdateFirmware : [] })),
    on(DeviceActions.getGWFirmwareUpdateSuccess, (state, payload) => ({ ...state, firmwareUpdate: payload.updateFirmware })),
    on(DeviceActions.getRbeAccessLogSuccess, (state, payload) => ({
        ...state,
        rbeAccessLog: payload.logs, rbeAccessLogPagination: payload.paging
    })),
    on(DeviceActions.getCallUnitEventLogSuccess, (state, payload) => ({
        ...state,
        callUnitLog: payload.logs, callUnitLogPagination: payload.paging
    })),
    on(DeviceActions.getRbeAccessLogExportSuccess, (state, payload) => ({
        ...state,
        rbeAccessLogExport: payload.logs,
    })),
    on(DeviceActions.resetRbeAccessLogExport, (state) => ({
        ...state,
        rbeAccessLogExport: undefined,
    })),
    on(DeviceActions.getAccessLogSuccess, (state, payload) => ({
        ...state,
        accessLog: payload.logs, accessLogPagination: payload.paging
    })),
    on(DeviceActions.getAccessLogExportSuccess, (state, payload) => ({
        ...state,
        accessLogExport: payload.logs,
    })),
    on(DeviceActions.resetAccessLogExport, (state) => ({
        ...state,
        accessLogExport: undefined,
    })),
    on(DeviceActions.getCallUnitLogExportSuccess, (state, payload) => ({
        ...state,
        callUnitLogExport: payload.logs,
    })),
    on(DeviceActions.resetCallUnitLogExport, (state) => ({
        ...state,
        callUnitLogExport: undefined,
    })),
    /*on(DeviceActions.deviceStateUpdateSuccess, (state, payload) => {
        if (Object.keys(payload.patch).length === 0) {
            return { ...state };
        }
        let deviceStatus = state.devicesStatus.find(ds => ds.Id === payload.deviceId);
        if (!deviceStatus) {
            deviceStatus = new DeviceStatus(payload.deviceId);
        }
        if (payload.patch !== undefined && payload.patch.CrpId !== null && payload.patch.CrpId !== undefined && deviceStatus.AutomationsStatus !== undefined) {
            const patch = deviceStatus.AutomationsStatus;
            const crp = payload.patch.CrpId;
            switch (crp) {
                case 1:
                    patch[1] = payload.patch.AutomationsStatus[0];
                    payload.patch.AutomationsStatus = patch;
                    break;
                case 2:
                    patch[3] = payload.patch.AutomationsStatus[0];
                    payload.patch.AutomationsStatus = patch;
                    break;
                case 3:
                    patch[5] = payload.patch.AutomationsStatus[0];
                    payload.patch.AutomationsStatus = patch;
                    break;
                case 4:
                    patch[7] = payload.patch.AutomationsStatus[0];
                    payload.patch.AutomationsStatus = patch;
                    break;
            }
            payload.patch.CrpId = null;
        }
        const newDeviceStatus = { ...deviceStatus, ...payload.patch };
        const newDevicesStatus = [...state.devicesStatus.filter(ds => ds.Id !== payload.deviceId), newDeviceStatus];
        if (state.currentDevice && state.currentDevice.Id === payload.deviceId) {
            return { ...state, devicesStatus: newDevicesStatus, currentDeviceStatus: newDeviceStatus };
        }
        return { ...state, devicesStatus: newDevicesStatus };
    }),*/
    on(DeviceActions.deviceStateUpdateSuccess, (state, payload) => {
        if (Object.keys(payload.patch).length === 0) {
            return { ...state };
        }
        let deviceStatus = state.devicesStatus.find(ds => ds.Id === payload.deviceId);
        if (!deviceStatus) {
            deviceStatus = new DeviceStatus(payload.deviceId);
        }
        if (payload.patch !== undefined && payload.patch.Inputs !== undefined) {
            const mapNew = new Map([...deviceStatus.Inputs.entries(), ...payload.patch.Inputs.entries()])
            payload.patch.Inputs = mapNew;
        }
        if (payload.patch !== undefined && payload.patch.Outputs !== undefined) {
            const mapNew = new Map([...deviceStatus.Outputs.entries(), ...payload.patch.Outputs.entries()])
            payload.patch.Outputs = mapNew;
        }
        if (payload.patch !== undefined && payload.patch.CrpId !== null && payload.patch.CrpId !== undefined && deviceStatus.AutomationsStatus !== undefined) {
            const patch = deviceStatus.AutomationsStatus;
            const crp = payload.patch.CrpId;
            switch (crp) {
                case 1:
                    patch[1] = payload.patch.AutomationsStatus[0];
                    payload.patch.AutomationsStatus = patch;
                    break;
                case 2:
                    patch[3] = payload.patch.AutomationsStatus[0];
                    payload.patch.AutomationsStatus = patch;
                    break;
                case 3:
                    patch[5] = payload.patch.AutomationsStatus[0];
                    payload.patch.AutomationsStatus = patch;
                    break;
                case 4:
                    patch[7] = payload.patch.AutomationsStatus[0];
                    payload.patch.AutomationsStatus = patch;
                    break;
            }
            payload.patch.CrpId = null;
        }
        const newDeviceStatus = { ...deviceStatus, ...payload.patch };
        const newDevicesStatus = [...state.devicesStatus.filter(ds => ds.Id !== payload.deviceId), newDeviceStatus];
        if (state.currentDevice && state.currentDevice.Id === payload.deviceId) {
            return { ...state, devicesStatus: newDevicesStatus, currentDeviceStatus: newDeviceStatus };
        }
        return { ...state, devicesStatus: newDevicesStatus };
    }),
    on(DeviceActions.resetDeviceTimerForm, (state) => ({
        ...state, deviceTimer: undefined, deviceTimerModify: false,
    })),
    // DEVICE TIMER
    on(DeviceActions.getDeviceTimersSuccess, (state, payload) => ({
        ...state, deviceTimers: payload.timers, deviceTimersMeta: payload.meta
    })),
    on(DeviceActions.getDeviceTimersInfo, (state) => ({
        ...state, deviceTimerModify: false
    })),
    on(DeviceActions.getDeviceTimersInfoSuccess, (state, payload) => ({
        ...state, deviceTimerInfo: payload.timersInfo, isSupportTimers: true,
    })),
    on(DeviceActions.getDeviceTimersInfoFailed, (state) => ({
        ...state, isSupportTimers: false
    })),
    on(DeviceActions.resetDeviceTimerForm, (state) => ({
        ...state, deviceTimer: undefined, deviceTimerModify: false,
    })),
    on(DeviceActions.addDeviceTimerSuccess, (state) => ({
        ...state, deviceTimer: undefined, deviceTimerModify: true
    })),
    on(DeviceActions.editDeviceTimerSuccess, (state) => ({
        ...state, deviceTimer: undefined, deviceTimerModify: true
    })),
    on(DeviceActions.deleteDeviceTimerSuccess, (state) => ({
        ...state, deviceTimer: undefined, deviceTimerModify: true
    })),
    on(DeviceActions.deleteDeviceAllTimerSuccess, (state) => ({
        ...state, deviceTimer: undefined, deviceTimerModify: true
    })),
    // DEVICE SPECIAL DAY
    on(DeviceActions.getDeviceSpecialDaysSuccess, (state, payload) => ({
        ...state, deviceSpecialDays: payload.specialDays, deviceSpecialDaysMeta: payload.meta
    })),
    on(DeviceActions.getDeviceSpecialDaysInfo, (state) => ({
        ...state, deviceSpecialDayModify: false
    })),
    on(DeviceActions.getDeviceSpecialDaysInfoSuccess, (state, payload) => ({
        ...state, deviceSpecialDayInfo: payload.specialDaysInfo
    })),
    on(DeviceActions.resetDeviceSpecialDayForm, (state) => ({
        ...state, deviceSpecialDay: undefined, deviceSpecialDayModify: false,
    })),
    on(DeviceActions.addDeviceSpecialDaySuccess, (state) => ({
        ...state, deviceSpecialDay: undefined, deviceSpecialDayModify: true
    })),
    on(DeviceActions.editDeviceSpecialDaySuccess, (state) => ({
        ...state, deviceSpecialDay: undefined, deviceSpecialDayModify: true
    })),
    on(DeviceActions.deleteDeviceSpecialDaySuccess, (state) => ({
        ...state, deviceSpecialDay: undefined, deviceSpecialDayModify: true
    })),
    on(DeviceActions.deleteDeviceAllSpecialDaySuccess, (state) => ({
        ...state, deviceSpecialDay: undefined, deviceSpecialDayModify: true
    })),
    on(DeviceActions.editNameAutomationConnectedSuccess, (state) => ({
        ...state, automationConnectedNameEdit: true
    })),
    // ------------------------- getStateDeviceByCommandId
    on(DeviceActions.getStateDeviceByCommandId, (state) => ({
        ...state, deviceState: undefined
    })),
    on(DeviceActions.getStateDeviceByCommandIdReset, (state) => ({
        ...state, deviceState: undefined
    })),
    on(DeviceActions.getStateDeviceByCommandIdSuccess, (state, payload) => ({
        ...state, deviceState: payload.deviceState
    })),
    on(DeviceActions.getStateDeviceByCommandIdSuccess, (state, payload) => {
        let deviceState4 = state.deviceState4;
        if (payload.deviceState.CommandId === 4) {
            deviceState4 = payload.deviceState;
        }
        return { ...state, deviceState: payload.deviceState, deviceState4: deviceState4 };
    }),
    // ------------------------- getStateDeviceByCommandId
    // ------------------------- getNewStateDeviceByCommandId
    on(DeviceActions.getNewStateDeviceByCommandId, (state) => ({
        ...state, newDeviceState: undefined
    })),
    on(DeviceActions.getNewStateDeviceByCommandIdReset, (state) => ({
        ...state, newDeviceState: undefined, hwVersion: null,
    })),
    on(DeviceActions.getNewStateDeviceByCommandIdSuccess, (state, payload) => {
        let fwVersion = state.fwVersion;
        if (payload.deviceState.FwVersion !== undefined) {
            fwVersion = payload.deviceState.FwVersion;
        }
        let motorBoardRxVersion = state.motorBoardRxVersion;
        if (payload.deviceState.MotorBoardRxVersion !== undefined) {
            motorBoardRxVersion = payload.deviceState.MotorBoardRxVersion;
        }
        let hwVersion = state.hwVersion;
        if (payload.deviceState.HwVersion !== undefined) {
            hwVersion = payload.deviceState.HwVersion;
        }
        return { ...state, newDeviceState: payload.deviceState, fwVersion: fwVersion, motorBoardRxVersion: motorBoardRxVersion, hwVersion: hwVersion };
    }),
    // ------------------------- getNewStateDeviceByCommandId
    // ------------------------- getNewStateDeviceByCommandId0
    on(DeviceActions.getNewStateDeviceByCommandId0, (state) => ({
        ...state, newDeviceState0: undefined
    })),
    on(DeviceActions.getNewStateDeviceByCommandId0Reset, (state) => ({
        ...state, newDeviceState0: undefined
    })),
    on(DeviceActions.getNewStateDeviceByCommandId0Success, (state, payload) => {
        return { ...state, newDeviceState0: payload.deviceState };
    }),
    // ------------------------- getNewStateDeviceByCommandId0
    on(DeviceActions.updateFirmwareVersion, (state, payload) => {
        const currentDeviceInfo = state.currentDeviceInfo;
        currentDeviceInfo.FwVersion = payload.fwVersion;
        return { ...state, currentDeviceInfo: currentDeviceInfo };
    }),
    on(DeviceActions.passwordVerifySupportReset, (state) => ({
        ...state, passwordRequest: false
    })),
    /*on(DeviceActions.passwordVerifySupportNot, (state) => ({
        ...state, passwordRequest: true
    })),*/
    /*on(DeviceActions.passwordVerifySupportFailed, (state) => ({
        ...state, passwordRequest: true
    })),*/
    on(DeviceActions.passwordVerifySupportNot, (state, payload) => {
        return { ...state, passwordRequest: true };
    }),
    on(DeviceActions.passwordVerifySupportFailed, (state, payload) => {
        return { ...state, passwordRequest: true };
    }),
    on(DeviceActions.passwordVerifySupportSuccess, (state, payload) => {
        if (!payload.supported) { // Password not supported, always access
            return { ...state, passwordValid: true, passwordRequest: true };
        }
        if (!payload.enabled) { // Password not active, always access
            return { ...state, passwordValid: true, passwordRequest: true };
        }
        return { ...state, passwordValid: payload.valid, passwordRequest: true };
    }),
    on(DeviceActions.passwordVerifyFailed, (state) => ({
        ...state, passwordValid: false,
    })),
    on(DeviceActions.passwordVerifySuccess, (state, payload) => {
        if (!payload.supported) { // Password not supported, always access
            return { ...state, passwordValid: true };
        }
        if (!payload.enabled) { // Password not active, always access
            return { ...state, passwordValid: true };
        }
        return { ...state, passwordValid: payload.valid };
    }),
    on(DeviceActions.passwordVerifyInvalid, (state, payload) => {
        if (!payload.supported) { // Password not supported, always access
            return { ...state, passwordValid: true };
        }
        if (!payload.enabled) { // Password not active, always access
            return { ...state, passwordValid: true };
        }
        return { ...state, passwordValid: payload.valid };
    }),
    on(DeviceActions.passwordVerifyFailed, (state) => ({
        ...state, passwordValid: false,
    })),
    on(DeviceActions.passwordChangeReset, (state) => ({
        ...state, passwordChange: false,
    })),
    on(DeviceActions.passwordChangeSuccess, (state) => ({
        ...state, passwordValid: false, passwordChange: true, passwordRequest: false,
    })),
    on(DeviceActions.passwordChangeFailed, (state) => ({
        ...state, passwordValid: false, passwordChange: false,
    })),

    //----------- Control Accesss

    // Groups
    on(DeviceActions.getGroupsSuccess, (state, payload) => ({
        ...state,
        groups: payload.groups, groupsPagination: payload.paging,
        groupModify: false, groupsMaxData: payload.maxData
    })),
    on(DeviceActions.resetGroupForm, (state) => ({
        ...state, group: undefined, groupModify: false, groupModifing: false,
    })),
    on(DeviceActions.addGroup, (state) => ({
        ...state, group: undefined, groupModify: false, groupModifing: true,
    })),
    on(DeviceActions.addGroupSuccess, (state) => ({
        ...state, group: undefined, groupModify: true, groupModifing: false,
    })),
    on(DeviceActions.addGroupFailed, (state) => ({
        ...state, group: undefined, groupModify: false, groupModifing: false,
    })),
    on(DeviceActions.editGroup, (state) => ({
        ...state, group: undefined, groupModify: false, groupModifing: true,
    })),
    on(DeviceActions.editGroupSuccess, (state) => ({
        ...state, group: undefined, groupModify: true, groupModifing: false,
    })),
    on(DeviceActions.editGroupFailed, (state) => ({
        ...state, group: undefined, groupModify: false, groupModifing: false,
    })),
    on(DeviceActions.deleteGroup, (state) => ({
        ...state, group: undefined, groupModify: false, groupModifing: true,
    })),
    on(DeviceActions.deleteGroupSuccess, (state) => ({
        ...state, group: undefined, groupModify: true, groupModifing: false,
    })),
    on(DeviceActions.deleteGroupFailed, (state) => ({
        ...state, group: undefined, groupModify: false, groupModifing: false,
    })),
    on(DeviceActions.getGroupsAllSuccess, (state, payload) => ({
        ...state,
        groupsAll: payload.groups
    })),
    // WeeklySchedules
    on(DeviceActions.getWeeklySchedulesSuccess, (state, payload) => ({
        ...state,
        weeklySchedules: payload.weeklySchedules, weeklySchedulesPagination: payload.paging,
        weeklyScheduleModify: false, weeklySchedulesMaxData: payload.maxData
    })),
    on(DeviceActions.getWeeklySchedulesAllSuccess, (state, payload) => ({
        ...state,
        weeklySchedulesAll: payload.weeklySchedules
    })),
    on(DeviceActions.resetWeeklyScheduleForm, (state) => ({
        ...state, weeklySchedule: undefined, weeklyScheduleModify: false, weeklyScheduleModifing: false,
    })),
    on(DeviceActions.addWeeklySchedule, (state) => ({
        ...state, weeklySchedule: undefined, weeklyScheduleModify: false, weeklyScheduleModifing: true,
    })),
    on(DeviceActions.addWeeklyScheduleSuccess, (state) => ({
        ...state, weeklySchedule: undefined, weeklyScheduleModify: true, weeklyScheduleModifing: false,
    })),
    on(DeviceActions.addWeeklyScheduleFailed, (state) => ({
        ...state, weeklySchedule: undefined, weeklyScheduleModify: false, weeklyScheduleModifing: false,
    })),
    on(DeviceActions.editWeeklySchedule, (state) => ({
        ...state, weeklySchedule: undefined, weeklyScheduleModify: false, weeklyScheduleModifing: true,
    })),
    on(DeviceActions.editWeeklyScheduleSuccess, (state) => ({
        ...state, weeklySchedule: undefined, weeklyScheduleModify: true, weeklyScheduleModifing: false,
    })),
    on(DeviceActions.editWeeklyScheduleFailed, (state) => ({
        ...state, weeklySchedule: undefined, weeklyScheduleModify: false, weeklyScheduleModifing: false,
    })),
    on(DeviceActions.deleteWeeklySchedule, (state) => ({
        ...state, weeklySchedule: undefined, weeklyScheduleModify: false, weeklyScheduleModifing: true,
    })),
    on(DeviceActions.deleteWeeklyScheduleSuccess, (state) => ({
        ...state, weeklySchedule: undefined, weeklyScheduleModify: true, weeklyScheduleModifing: false,
    })),
    on(DeviceActions.deleteWeeklyScheduleFailed, (state) => ({
        ...state, weeklySchedule: undefined, weeklyScheduleModify: false, weeklyScheduleModifing: false,
    })),
    // SpecialPlanning
    on(DeviceActions.getSpecialPlanningsSuccess, (state, payload) => ({
        ...state,
        specialPlannings: payload.specialPlannings, specialPlanningsPagination: payload.paging,
        specialPlanningModify: false, specialPlanningsMaxData: payload.maxData
    })),
    on(DeviceActions.getSpecialPlanningsAllSuccess, (state, payload) => ({
        ...state,
        specialPlanningsAll: payload.specialPlannings
    })),
    on(DeviceActions.resetSpecialPlanningForm, (state) => ({
        ...state, specialPlanning: undefined, specialPlanningModify: false, specialPlanningModifing: false,
    })),
    on(DeviceActions.addSpecialPlanning, (state) => ({
        ...state, specialPlanning: undefined, specialPlanningModify: false, specialPlanningModifing: true,
    })),
    on(DeviceActions.addSpecialPlanningSuccess, (state) => ({
        ...state, specialPlanning: undefined, specialPlanningModify: true, specialPlanningModifing: false,
    })),
    on(DeviceActions.addSpecialPlanningFailed, (state) => ({
        ...state, specialPlanning: undefined, specialPlanningModify: false, specialPlanningModifing: false,
    })),
    on(DeviceActions.editSpecialPlanning, (state) => ({
        ...state, specialPlanning: undefined, specialPlanningModify: false, specialPlanningModifing: true,
    })),
    on(DeviceActions.editSpecialPlanningSuccess, (state) => ({
        ...state, specialPlanning: undefined, specialPlanningModify: true, specialPlanningModifing: false,
    })),
    on(DeviceActions.editSpecialPlanningFailed, (state) => ({
        ...state, specialPlanning: undefined, specialPlanningModify: false, specialPlanningModifing: false,
    })),
    on(DeviceActions.deleteSpecialPlanning, (state) => ({
        ...state, specialPlanning: undefined, specialPlanningModify: false, specialPlanningModifing: true,
    })),
    on(DeviceActions.deleteSpecialPlanningSuccess, (state) => ({
        ...state, specialPlanning: undefined, specialPlanningModify: true, specialPlanningModifing: false,
    })),
    on(DeviceActions.deleteSpecialPlanningFailed, (state) => ({
        ...state, specialPlanning: undefined, specialPlanningModify: false, specialPlanningModifing: false,
    })),
    on(DeviceActions.acRbeResetSuccess, (state) => ({
        ...state, specialPlanningModify: true, weeklyScheduleModify: true,
    })),
    //----------- Control Accesss

    on(DeviceActions.getLogThExportReset, (state) => ({
        ...state,
        loadingThLogExp: false,
        loadedThLogExp: false,
        logThExport: new LogThExport(),
    })),
    on(DeviceActions.getLogThExport, (state) => ({
        ...state,
        loadingThLogExp: true,
        loadedThLogExp: false,
        logThExport: new LogThExport(),
    })),
    on(DeviceActions.getLogThExportSuccess, (state, payload) => ({
        ...state,
        loadingThLogExp: false,
        loadedThLogExp: true,
        logThExport: payload.logThExport,
    })),
    on(DeviceActions.getLogThExportFailed, (state) => ({
        ...state,
        loadingThLogExp: false,
        loadedThLogExp: false,
        logThExport: new LogThExport(),
    })),
    on(DeviceActions.toggleRemoteClientLock, (state) => ({ ...state, remoteClientLockingUnlocking: true, remoteClientLockedUnlocked: false })),
    on(DeviceActions.toggleRemoteClientLockSuccess, (state) => ({ ...state, remoteClientLockingUnlocking: false, remoteClientLockedUnlocked: true })),
    on(DeviceActions.toggleRemoteClientLockFailed, (state) => ({ ...state, remoteClientLockingUnlocking: false, remoteClientLockedUnlocked: false })),
    on(DeviceActions.resetFinalUserRemoteClients, (state) => ({ ...state, remoteClientDeleting: false, remoteClientDeleted: false, remoteClientLockingUnlocking: false, remoteClientLockedUnlocked: false })),

    //----------- Control Access MTMA
    // Mtma Groups
    on(DeviceActions.getMtmaGroupsSuccess, (state, payload) => ({
        ...state,
        mtmaGroups: payload.groups, mtmaGroupsPagination: payload.paging,
        mtmaGroupModify: false, mtmaGroupsMaxData: payload.maxData
    })),
    on(DeviceActions.resetMtmaGroupForm, (state) => ({
        ...state, mtmaGroup: undefined, mtmaGroupModify: false,
    })),
    on(DeviceActions.addMtmaGroupSuccess, (state) => ({
        ...state, mtmaGroup: undefined, mtmaGroupModify: true
    })),
    on(DeviceActions.editMtmaGroupSuccess, (state) => ({
        ...state, mtmaGroup: undefined, mtmaGroupModify: true
    })),
    on(DeviceActions.deleteMtmaGroupSuccess, (state) => ({
        ...state, mtmaGroup: undefined, mtmaGroupModify: true
    })),
    on(DeviceActions.getMtmaGroupsAllSuccess, (state, payload) => ({
        ...state,
        mtmaGroupsAll: payload.groups
    })),
    // MTMA WeeklySchedules
    on(DeviceActions.getMtmaWeeklySchedulesSuccess, (state, payload) => ({
        ...state,
        mtmaWeeklySchedules: payload.weeklySchedules, mtmaWeeklySchedulesPagination: payload.paging,
        mtmaWeeklyScheduleModify: false, mtmaWeeklySchedulesMaxData: payload.maxData
    })),
    on(DeviceActions.getMtmaWeeklySchedulesAllSuccess, (state, payload) => ({
        ...state,
        mtmaWeeklySchedulesAll: payload.weeklySchedules
    })),
    on(DeviceActions.resetMtmaWeeklyScheduleForm, (state) => ({
        ...state, mtmaWeeklySchedule: undefined, mtmaWeeklyScheduleModify: false,
    })),
    on(DeviceActions.addMtmaWeeklyScheduleSuccess, (state) => ({
        ...state, mtmaWeeklySchedule: undefined, mtmaWeeklyScheduleModify: true
    })),
    on(DeviceActions.editMtmaWeeklyScheduleSuccess, (state) => ({
        ...state, mtmaWeeklySchedule: undefined, mtmaWeeklyScheduleModify: true
    })),
    on(DeviceActions.deleteMtmaWeeklyScheduleSuccess, (state) => ({
        ...state, mtmaWeeklySchedule: undefined, mtmaWeeklyScheduleModify: true,
    })),
    // MTMA SpecialPlanning
    on(DeviceActions.getMtmaSpecialPlanningsSuccess, (state, payload) => ({
        ...state,
        mtmaSpecialPlannings: payload.specialPlannings, mtmaSpecialPlanningsPagination: payload.paging,
        mtmaSpecialPlanningModify: false, mtmaSpecialPlanningsMaxData: payload.maxData
    })),
    on(DeviceActions.getMtmaSpecialPlanningsAllSuccess, (state, payload) => ({
        ...state,
        mtmaSpecialPlanningsAll: payload.specialPlannings
    })),
    on(DeviceActions.resetMtmaSpecialPlanningForm, (state) => ({
        ...state, mtmaSpecialPlanning: undefined, mtmaSpecialPlanningModify: false,
    })),
    on(DeviceActions.addMtmaSpecialPlanningSuccess, (state) => ({
        ...state, mtmaSpecialPlanning: undefined, mtmaSpecialPlanningModify: true
    })),
    on(DeviceActions.editMtmaSpecialPlanningSuccess, (state) => ({
        ...state, mtmaSpecialPlanning: undefined, mtmaSpecialPlanningModify: true
    })),
    on(DeviceActions.deleteMtmaSpecialPlanningSuccess, (state) => ({
        ...state, mtmaSpecialPlanning: undefined, mtmaSpecialPlanningModify: true
    })),
    //----------- Control Accesss MTMA
    //SETTINGS PAGING
    on(DeviceActions.getDeviceSettingsPagingReset, (state, payload) => ({
        ...state, currentDeviceSettingsCategoriesPaging: undefined, currentDeviceSettingsCategoriesPagingLoading: false, currentDeviceSettingsCategoriesPagingError: false,
    })),
    on(DeviceActions.getDeviceSettingsPaging, (state, payload) => ({
        ...state, currentDeviceSettingsCategoriesPagingLoading: true,
    })),
    on(DeviceActions.getDeviceSettingsPagingSuccess, (state, payload) => ({
        ...state, currentDeviceSettingsCategoriesPaging: payload.SettingsCategoriesPaging,
    })),
    on(DeviceActions.getDeviceSettingsPagingFailed, (state, payload) => ({
        ...state, currentDeviceSettingsCategoriesPaging: undefined, currentDeviceSettingsCategoriesPagingLoading: false, currentDeviceSettingsCategoriesPagingError: true,
    })),
    //SETTINGS PAGING
    on(DeviceActions.acRbeSync, (state) => ({ ...state, acRbeSyncing: true, acRbeSynced: false })),
    on(DeviceActions.acRbeSyncSuccess, (state) => ({ ...state, acRbeSyncing: false, acRbeSynced: true })),
    on(DeviceActions.acRbeSyncFailed, (state) => ({ ...state, acRbeSyncing: false, acRbeSynced: true })),

    // Get TH Log
    on(DeviceActions.getThLog, (state) => ({ ...state, thLogLoading: true, thLogData: null })),
    on(DeviceActions.getThLogSuccess, (state, payload) => ({ ...state, thLogLoading: false, thLogData: payload.thermoLog })),
    on(DeviceActions.getThLogFailed, (state) => ({ ...state, thLogLoading: false, thLogData: null })),
    on(DeviceActions.getAutomationsFirmwaresSuccess, (state, payload) => ({ ...state, automationsFirmwaresList: payload.automationsFirmwaresList })),

    on(DeviceActions.postFirmwareUpdate, (state) => ({ ...state, fwUpdateStart: true })),
    on(DeviceActions.postFirmwareUpdateSuccess, (state) => ({ ...state, fwUpdateStart: false })),
    on(DeviceActions.postFirmwareUpdateFailed, (state) => ({ ...state, fwUpdateStart: false })),


    on(DeviceActions.updateDeviceAutomationRtc, (state) => ({ ...state, updatedDeviceAutomationRtc: false })),
    on(DeviceActions.updateDeviceAutomationRtcSuccess, (state) => ({ ...state, updatedDeviceAutomationRtc: true })),

    on(DeviceActions.getAutomationsAliasSuccess, (state, payload) => ({ ...state, automationAlias: payload.automationsAlias })),
    on(DeviceActions.resetEditAutomationsAlias, (state) => ({ ...state, automationAliasEditing: false, automationAliasEdited: false })),
    on(DeviceActions.editAutomationsAlias, (state) => ({ ...state, automationAliasEditing: true, automationAliasEdited: false })),
    on(DeviceActions.editAutomationsAliasSuccess, (state) => ({ ...state, automationAliasEditing: false, automationAliasEdited: true })),
    on(DeviceActions.getAutomationsTextSuccess, (state, payload) => ({ ...state, automationsText: payload.automationsText })),
    on(DeviceActions.getMtmaPermissionsSuccess, (state, payload) => ({
        ...state,
        mtmaPermissions: payload.permissions, mtmaPermissionsPagination: payload.paging, mtmaPermissionsMaxData: payload.maxData
    })),
);

export const reducer = (state: State | undefined, action: Action): State => deviceReducer(state, action);
